/**
 * CatalogoRiesgosRepo sobre Supabase. Singleton por gerencia (RBS, escalas,
 * umbrales). Compartido: los ajustes del catálogo valen para todos.
 */

import type { CatalogoRiesgosRepo } from "../../repos/CatalogoRiesgosRepo";
import type { CatalogoRiesgos } from "@app-types/entities";
import type { GerenciaCodigo } from "@constants/gerencias";
import { OptimisticLockError } from "../../repos/BaseRepo";
import {
  RBS_DEFAULT,
  ESCALA_PROBABILIDAD_DEFAULT,
  ESCALA_IMPACTO_DEFAULT,
  UMBRALES_NIVEL_DEFAULT,
} from "@features/riesgos/catalogoDefault";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

export class SupabaseCatalogoRiesgosRepo
  extends SupabaseBaseRepo<CatalogoRiesgos>
  implements CatalogoRiesgosRepo
{
  constructor(tenantId: string) {
    super("catalogoRiesgos", tenantId);
  }

  async getOrInit(userEmail: string, gerenciaId?: GerenciaCodigo): Promise<CatalogoRiesgos> {
    const filas = await this.listAll();
    if (gerenciaId) {
      const enGerencia = filas.find((f) => f.gerenciaId === gerenciaId);
      if (enGerencia) return enGerencia;
    } else if (filas[0]) {
      return filas[0];
    }
    const now = new Date().toISOString();
    const nuevo: CatalogoRiesgos = {
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      ...(gerenciaId ? { gerenciaId } : {}),
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: userEmail,
      updatedBy: userEmail,
      rbs: [...RBS_DEFAULT],
      escalaProbabilidad: [...ESCALA_PROBABILIDAD_DEFAULT],
      escalaImpacto: {
        costo: [...ESCALA_IMPACTO_DEFAULT.costo],
        cronograma: [...ESCALA_IMPACTO_DEFAULT.cronograma],
        alcance: [...ESCALA_IMPACTO_DEFAULT.alcance],
        calidad: [...ESCALA_IMPACTO_DEFAULT.calidad],
        hse: [...ESCALA_IMPACTO_DEFAULT.hse],
      },
      umbralesNivel: [...UMBRALES_NIVEL_DEFAULT],
    };
    await this.createRaw(nuevo);
    return nuevo;
  }

  async update(
    data: Omit<
      CatalogoRiesgos,
      "id" | "tenantId" | "createdAt" | "updatedAt" | "version" | "createdBy" | "updatedBy"
    >,
    expectedVersion: number,
    userEmail: string,
    gerenciaId?: GerenciaCodigo,
  ): Promise<CatalogoRiesgos> {
    const current = await this.getOrInit(userEmail, gerenciaId);
    if (current.version !== expectedVersion) {
      throw new OptimisticLockError(current.id, expectedVersion, current.version);
    }
    const now = new Date().toISOString();
    const updated: CatalogoRiesgos = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: now,
      updatedBy: userEmail,
    };
    await this.updateRaw(updated, expectedVersion);
    return updated;
  }
}
